import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Button, Switch, Space } from 'antd';
import {
  buildFilterSelector,
  filterDrawerTitleSelector,
  isRTTrendsSelector,
} from '@store/trends/base/selectors';
import { applyFilter, updateRule, removeRule, resetFilter } from '@store/trends/base/actions';
import { tid } from '@utils/test';
import { useNewFilterDialogEnabled } from '@src/utils/filter';
import { AdvancedFilterBuilder } from '@src/components/filter/containers/AdvancedFilterBuilder';
import FilterBar from './FilterBar';
import FilterBarAdvanced from './FilterBarAdvanced';
import { FilterBarFooter, StyledDrawer } from './styled';
import { modeAtom, TABLE_MODES } from '../atoms';
import { useAtom } from 'jotai';

function FilterBuilder() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const generatedFilter = useSelector(buildFilterSelector);
  const title = useSelector(filterDrawerTitleSelector);
  const isRTTrends = useSelector(isRTTrendsSelector);
  const newFilterDialogEnabled = useNewFilterDialogEnabled();
  const [advanced, setAdvanced] = useState(true);

  const [mode, setMode] = useAtom(modeAtom);
  const visible = mode === TABLE_MODES.filter;

  const onToggle = (value) => {
    const newMode = value ? TABLE_MODES.filter : TABLE_MODES.drilldown;
    setMode(newMode);
  };

  const { rule = [] } = generatedFilter;

  const handleOk = () => {
    dispatch(applyFilter({ navigate }));
    handleCancel();
  };

  const handleCancel = () => {
    onToggle(false);
    dispatch(resetFilter());
  };

  const onSwitchChange = (checked: boolean) => {
    setAdvanced(checked);
  };

  const getFilterBarComponent = () => {
    if (rule.length === 0) return <p>Click on a row to add to the filter</p>;

    if (newFilterDialogEnabled) {
      return (
        <AdvancedFilterBuilder
          value={rule as any[]}
          onRuleRemove={removeRule}
          onRuleUpdate={updateRule}
        />
      );
    }

    return advanced ? (
      <FilterBarAdvanced rules={rule} removeRule={removeRule} updateRule={updateRule} />
    ) : (
      <FilterBar rules={rule} removeRule={removeRule} />
    );
  };

  if (newFilterDialogEnabled && !isRTTrends) return null;

  return (
    <StyledDrawer
      title={
        <Space style={{ alignItems: 'baseline' }}>
          <h3>{title}</h3>
          {!newFilterDialogEnabled && (
            <Switch
              checkedChildren="Advanced"
              unCheckedChildren="Basic"
              onChange={onSwitchChange}
              defaultChecked
              {...tid('filter-drawer-switch')}
            />
          )}
        </Space>
      }
      placement="top"
      closable={true}
      onClose={handleCancel}
      open={visible}
      key="filter-builder"
      mask={false}
      height={200}
      zIndex={999}
      footer={
        <FilterBarFooter {...tid('filter-drawer-footer')}>
          <Button type="primary" onClick={handleOk}>
            Apply
          </Button>
          <Button onClick={handleCancel}>Cancel</Button>
        </FilterBarFooter>
      }
    >
      <div {...tid('filter-drawer-body')}>{getFilterBarComponent()}</div>
    </StyledDrawer>
  );
}

export default FilterBuilder;
